import { Link } from "@tanstack/react-router";
import type { ReactNode } from "react";

/**
 * Editorial body renderer for database-backed articles.
 *
 * The stored body is an array of paragraphs. Two lightweight conventions are
 * supported so writers can structure long-form pieces without a markdown
 * dependency:
 *
 *   "## Section title"        → an <h2> reading anchor
 *   "[label](/internal/path)" → an internal link (external http(s) allowed)
 *
 * Everything else renders as plain prose.
 */

const LINK = /\[([^\]]+)\]\(([^)\s]+)\)/g;

function renderInline(text: string, keyPrefix: string): ReactNode[] {
  const out: ReactNode[] = [];
  let last = 0;
  let match: RegExpExecArray | null;
  LINK.lastIndex = 0;
  while ((match = LINK.exec(text)) !== null) {
    if (match.index > last) out.push(text.slice(last, match.index));
    const [, label, href] = match;
    const key = `${keyPrefix}-${match.index}`;
    const className =
      "text-gold underline decoration-gold/40 underline-offset-4 hover:text-gold-luminous hover:decoration-gold";
    if (href.startsWith("http")) {
      out.push(
        <a key={key} href={href} rel="noopener noreferrer" className={className}>
          {label}
        </a>,
      );
    } else {
      out.push(
        <Link key={key} to={href as never} className={className}>
          {label}
        </Link>,
      );
    }
    last = match.index + match[0].length;
  }
  if (last < text.length) out.push(text.slice(last));
  return out;
}

export function ArticleBody({ body }: { body: string[] }) {
  return (
    <div className="mt-12 space-y-6 text-lg leading-relaxed text-ivory/85">
      {body.map((para, i) => {
        if (para.startsWith("## ")) {
          return (
            <h2 key={i} className="pt-6 font-serif text-2xl text-gold-luminous sm:text-3xl">
              {para.slice(3)}
            </h2>
          );
        }
        return <p key={i}>{renderInline(para, String(i))}</p>;
      })}
    </div>
  );
}

export default ArticleBody;
